import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../styles/memora_op.css';

const API = process.env.REACT_APP_API_BASE;

const NIVEL_CFG = {
  'Crítico': { cor: '#dc3545', badge: 'bg-danger',              icon: 'fas fa-skull-crossbones' },
  'Alto':    { cor: '#fd7e14', badge: 'bg-warning text-dark',   icon: 'fas fa-exclamation-triangle' },
  'Médio':   { cor: '#ffc107', badge: 'bg-warning text-dark',   icon: 'fas fa-exclamation-circle' },
  'Baixo':   { cor: '#198754', badge: 'bg-success',             icon: 'fas fa-check-circle' },
};

const fmtData = (d) => {
  if (!d) return '-';
  const p = String(d).slice(0, 10).split('-');
  return p.length < 3 ? d : `${p[2]}/${p[1]}/${p[0]}`;
};

export default function MemoraRiscos() {
  const navigate = useNavigate();
  const [clientes, setClientes]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [erro, setErro]                   = useState('');
  const [filtroNivel, setFiltroNivel]     = useState('Todos');
  const [busca, setBusca]                 = useState('');
  const [selecionado, setSelecionado]     = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('access');
    setLoading(true);
    axios
      .get(`${API}/api/reunioes/riscos/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (res.data.success) setClientes(res.data.clientes || []);
        else setErro(res.data.erro || 'Erro ao carregar dados');
      })
      .catch(() => setErro('Erro de conexão com o servidor'))
      .finally(() => setLoading(false));
  }, []);

  const kpis = useMemo(() => [
    { label: 'Total Monitorados', valor: clientes.length,                                          cor: '#00205b', icon: 'fa-users' },
    { label: 'Críticos',          valor: clientes.filter(c => c.nivel === 'Crítico').length,       cor: '#dc3545', icon: 'fa-skull-crossbones' },
    { label: 'Alto Risco',        valor: clientes.filter(c => c.nivel === 'Alto').length,          cor: '#fd7e14', icon: 'fa-exclamation-triangle' },
    { label: 'Médio / Baixo',     valor: clientes.filter(c => c.nivel === 'Médio' || c.nivel === 'Baixo').length, cor: '#198754', icon: 'fa-check-circle' },
  ], [clientes]);

  const filtrados = useMemo(() =>
    clientes.filter(c => {
      const ok1 = filtroNivel === 'Todos' || c.nivel === filtroNivel;
      const ok2 = !busca || c.cliente_empresa.toLowerCase().includes(busca.toLowerCase());
      return ok1 && ok2;
    }),
  [clientes, filtroNivel, busca]);

  return (
    <div className="das-page">
      <div className="container-fluid mt-4">

        {/* ── Header ── */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <div className="mb-4">
                  <span className="badge bg-light text-dark me-2" style={{ fontSize: '20px', fontWeight: '500' }}>
                    Gestão de Riscos — Memor.IA
                  </span>
                  <span className="badge bg-danger text-white" style={{ fontSize: '15px', fontWeight: '500' }}>
                    <i className="fas fa-shield-alt me-1"></i>Churn Prevention
                  </span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <i className="fas fa-exclamation-triangle header-icon me-2"></i>
                  <h2 className="titulo-pagina m-0">Clientes em Risco</h2>
                </div>
                <p className="text-black-50 mb-0">
                  Monitoramento automático de sinais de churn com score de risco 0–100
                </p>
              </div>
              <div className="mb-2">
                <button
                  className="btn btn-sm"
                  onClick={() => navigate('/analise-gestor-memora')}
                  style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' }}
                >
                  <i className="fas fa-arrow-left me-1"></i>Voltar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── KPIs ── */}
        <div className="row g-3 mb-4">
          {kpis.map((k, i) => (
            <div key={i} className="col-6 col-md-3">
              <div className="card border-0 shadow-sm h-100" style={{ borderTop: `3px solid ${k.cor}` }}>
                <div className="card-body d-flex align-items-center gap-2 py-3 px-3">
                  <div style={{
                    background: `${k.cor}1a`, borderRadius: 12,
                    width: 46, height: 46, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className={`fas ${k.icon}`} style={{ color: k.cor, fontSize: '1.2rem' }}></i>
                  </div>
                  <div>
                    <div className="fw-bold lh-1 mb-1" style={{ fontSize: '1.5rem', color: k.cor }}>{k.valor}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{k.label}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filtros ── */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body py-2 px-3 d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <div className="d-flex gap-2 flex-wrap">
              {['Todos', 'Crítico', 'Alto', 'Médio', 'Baixo'].map(nivel => (
                <button
                  key={nivel}
                  className={`btn btn-sm ${filtroNivel === nivel ? 'btn-dark' : 'btn-outline-secondary'}`}
                  onClick={() => setFiltroNivel(nivel)}
                >
                  {nivel === 'Todos' ? 'Todos' : (
                    <>
                      <span className={`badge ${NIVEL_CFG[nivel]?.badge || 'bg-secondary'} me-1`}>
                        {clientes.filter(c => c.nivel === nivel).length}
                      </span>
                      {nivel}
                    </>
                  )}
                </button>
              ))}
            </div>
            <input
              className="form-control form-control-sm"
              style={{ maxWidth: 240 }}
              placeholder="Buscar cliente..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
            />
          </div>
        </div>

        {/* ── Tabela ── */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" />
            <p className="text-muted mt-2">Calculando scores de risco...</p>
          </div>
        ) : erro ? (
          <div className="alert alert-danger">{erro}</div>
        ) : filtrados.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="fas fa-check-circle fa-3x mb-3 text-success"></i>
            <p>Nenhum cliente encontrado com os filtros selecionados.</p>
          </div>
        ) : (
          <div className="card border-0 shadow-sm">
            <div
              className="card-header bg-white d-flex align-items-center"
              style={{ borderBottom: '2px solid #00205b' }}
            >
              <i className="fas fa-table me-2" style={{ color: '#00205b' }}></i>
              <strong>Score de Risco por Cliente</strong>
              <span className="badge bg-secondary ms-2">{filtrados.length}</span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Cliente</th>
                      <th style={{ width: 150 }}>Score</th>
                      <th>Nível</th>
                      <th>Última Reunião</th>
                      <th>Sem Contato</th>
                      <th>Nota Média</th>
                      <th>Operador</th>
                      <th>Principal Motivo</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtrados.map((c, i) => {
                      const cfg       = NIVEL_CFG[c.nivel] || {};
                      const topMotivo = c.motivos?.length > 0 ? c.motivos[0].indicador : '—';
                      return (
                        <tr key={i} style={{ cursor: 'pointer' }} onClick={() => setSelecionado(c)}>
                          <td className="fw-semibold">{c.cliente_empresa}</td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <div style={{
                                flex: 1, background: '#e9ecef',
                                borderRadius: 4, height: 8, overflow: 'hidden',
                              }}>
                                <div style={{
                                  width: `${c.score}%`,
                                  background: cfg.cor || '#00205b',
                                  height: '100%', transition: 'width .4s',
                                }} />
                              </div>
                              <span className="fw-bold" style={{ minWidth: 28, color: cfg.cor }}>{c.score}</span>
                            </div>
                          </td>
                          <td>
                            <span className={`badge ${cfg.badge || 'bg-secondary'}`}>
                              <i className={`${cfg.icon || 'fas fa-circle'} me-1`}></i>{c.nivel}
                            </span>
                          </td>
                          <td>{fmtData(c.ultima_reuniao)}</td>
                          <td>
                            <span className={
                              c.dias_sem_reuniao > 30 ? 'text-danger fw-bold' :
                              c.dias_sem_reuniao > 15 ? 'text-warning fw-semibold' : 'text-success'
                            }>
                              {c.dias_sem_reuniao ?? '—'}d
                            </span>
                          </td>
                          <td>{c.media_nota != null ? c.media_nota.toFixed(1) : '—'}</td>
                          <td>{c.operador}</td>
                          <td>
                            <span className="text-muted" style={{ fontSize: '0.82rem' }}>{topMotivo}</span>
                          </td>
                          <td>
                            <button className="btn btn-sm btn-outline-primary px-2 py-0">Ver</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal de detalhe ── */}
        {selecionado && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
            onClick={() => setSelecionado(null)}
          >
            <div
              className="modal-dialog modal-dialog-scrollable"
              style={{ maxWidth: 680 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}
                >
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>{selecionado.cliente_empresa}</h5>
                    <span className={`badge ${NIVEL_CFG[selecionado.nivel]?.badge || 'bg-secondary'} me-2`}>
                      Risco {selecionado.nivel}
                    </span>
                    <span className="badge bg-light text-dark">Score {selecionado.score}/100</span>
                  </div>
                  <button className="btn-close" onClick={() => setSelecionado(null)} />
                </div>

                <div className="modal-body">
                  {/* Score bar */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small className="text-muted">Score de Risco</small>
                      <strong style={{ color: NIVEL_CFG[selecionado.nivel]?.cor }}>
                        {selecionado.score}/100
                      </strong>
                    </div>
                    <div style={{ background: '#e9ecef', borderRadius: 6, height: 14, overflow: 'hidden' }}>
                      <div style={{
                        width: `${selecionado.score}%`,
                        background: NIVEL_CFG[selecionado.nivel]?.cor,
                        height: '100%', transition: 'width .4s',
                      }} />
                    </div>
                    <div className="d-flex justify-content-between mt-1">
                      <small className="text-success">0 — Baixo</small>
                      <small className="text-warning">30 — Médio</small>
                      <small className="text-warning">60 — Alto</small>
                      <small className="text-danger">80 — Crítico</small>
                    </div>
                  </div>

                  {/* Info cards */}
                  <div className="row g-2 mb-3">
                    {[
                      { label: 'Reuniões',      val: selecionado.total_reunioes },
                      { label: 'Última Reunião', val: fmtData(selecionado.ultima_reuniao) },
                      {
                        label: 'Sem Contato',
                        val: `${selecionado.dias_sem_reuniao ?? '—'}d`,
                        color: selecionado.dias_sem_reuniao > 30 ? '#dc3545' : '#198754',
                      },
                      { label: 'Nota Média', val: selecionado.media_nota?.toFixed(1) ?? '—' },
                    ].map((item, idx) => (
                      <div key={idx} className="col-6 col-md-3">
                        <div className="p-2 rounded text-center" style={{ background: '#f8f9fa' }}>
                          <div className="fw-bold" style={item.color ? { color: item.color } : {}}>
                            {item.val}
                          </div>
                          <small className="text-muted">{item.label}</small>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Indicadores */}
                  {selecionado.motivos?.length > 0 && (
                    <div className="mb-3">
                      <h6 className="fw-bold mb-2">
                        <i className="fas fa-list-alt me-2" style={{ color: '#00205b' }}></i>
                        Indicadores de Risco
                      </h6>
                      {selecionado.motivos.map((m, idx) => (
                        <div
                          key={idx}
                          className="d-flex align-items-center gap-2 mb-2 p-2 rounded"
                          style={{ background: '#fff3cd', border: '1px solid #ffc10733' }}
                        >
                          <span className="badge bg-warning text-dark flex-shrink-0">+{m.peso}</span>
                          <div>
                            <div className="fw-semibold" style={{ fontSize: '0.88rem' }}>{m.indicador}</div>
                            <div className="text-muted" style={{ fontSize: '0.78rem' }}>{m.detalhe}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Ações */}
                  {selecionado.acoes_recomendadas?.length > 0 && (
                    <div className="mb-3">
                      <h6 className="fw-bold mb-2">
                        <i className="fas fa-lightbulb me-2" style={{ color: '#fd7e14' }}></i>
                        Ações Recomendadas
                      </h6>
                      <ul className="list-unstyled mb-0">
                        {selecionado.acoes_recomendadas.map((a, idx) => (
                          <li key={idx} className="d-flex gap-2 mb-1">
                            <i
                              className="fas fa-chevron-right mt-1 flex-shrink-0"
                              style={{ color: '#00205b', fontSize: '0.7rem' }}
                            ></i>
                            <span style={{ fontSize: '0.88rem' }}>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Objeções */}
                  {selecionado.top_objecoes?.length > 0 && (
                    <div>
                      <h6 className="fw-bold mb-2">
                        <i className="fas fa-ban me-2" style={{ color: '#dc3545' }}></i>
                        Objeções Mais Frequentes
                      </h6>
                      {selecionado.top_objecoes.map((o, idx) => (
                        <div
                          key={idx}
                          className="d-flex justify-content-between align-items-center mb-1 p-2 rounded"
                          style={{ background: '#f8f9fa' }}
                        >
                          <span style={{ fontSize: '0.85rem' }}>{o.texto}</span>
                          <span className="badge bg-secondary">{o.count}x</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <small className="text-muted me-auto">
                    <i className="fas fa-user me-1"></i>Operador: {selecionado.operador}
                  </small>
                  <button className="btn btn-sm btn-secondary" onClick={() => setSelecionado(null)}>
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
